import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useAuthStore } from "@/store/auth-store";
import { useToast } from "@/hooks/use-toast";
import { AppDialog } from "@/components/ui/AppDialog";
import { useScreeningRecorder } from "@/hooks/useScreeningRecorder";
import { Movable } from "@/components/viewer/Movable";
import { RoomViewer } from "@/components/viewer/RoomViewer";
import {
  advanceScreening,
  beginScreening,
  fetchScreeningState,
  finaliseVideo,
  type ScreeningState,
} from "@/api/tests-api";

/**
 * The video screening round, from the candidate's side.
 *
 * Built to `docs/design/Candidate Screening - Standalone.html`, whose
 * values are transcribed into `.sk-vr-*` in styles/components.css rather
 * than written here as Tailwind: `index.css` re-declares `text-[10px]`
 * through `text-[13px]` 15% larger, so arbitrary sizes would bump some
 * of the design's type and not the rest.
 *
 * WHAT THE DESIGN DOES THAT THIS DOES NOT, all on instruction:
 *
 * * its section tabs jump between sections — here the stepper is state,
 *   not a control, and advances only on NEXT or the clock;
 * * Retake, Skip and Save & exit are gone. One attempt, no going back;
 * * a question's row shows Play once answered. There is no playback at
 *   all: the recording goes to the server and that is the end of the
 *   candidate's part;
 * * future questions are not listed. The design shows every question in
 *   a section; revealing them early would let a candidate prepare an
 *   answer they are supposed to give unrehearsed, which is the point of
 *   the round.
 *
 * THE CLOCK ON SCREEN IS NOT THE CLOCK THAT DECIDES. The server stamps
 * when a question was served and closes it when its allowance is spent;
 * this counts down from the number the server last sent, re-anchoring
 * every time it answers. A candidate who stops the page's timer buys
 * nothing.
 */

const POLL_MS = 15_000;

function mmss(total: number): string {
  const s = Math.max(0, Math.round(total));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

/**
 * Initials for the badge, the way the design renders them.
 *
 * HONORIFICS ARE SKIPPED, and the design settles it rather than taste:
 * its own sample is "Dr Anaya Rehman" shown as "AR", not "DR". Nearly
 * every candidate here is a doctor, so keeping the title would put the
 * same two letters on every badge in the system and the one thing it is
 * for -- telling one candidate from another at a glance -- would stop
 * working.
 */
const HONORIFICS = new Set([
  "dr", "dr.", "doctor", "prof", "prof.", "professor",
  "mr", "mr.", "mrs", "mrs.", "ms", "ms.", "miss", "mx", "mx.",
]);

function initialsOf(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .filter((part) => !HONORIFICS.has(part.toLowerCase()));
  if (parts.length === 0) return "--";
  const first = parts[0]![0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]![0] ?? "") : "";
  return (first + last).toUpperCase();
}

export function VideoScreeningPage() {
  const { attemptId = "" } = useParams<{ attemptId: string }>();
  const user = useAuthStore((s) => s.user);
  const { toast } = useToast();
  const navigate = useNavigate();

  const recorder = useScreeningRecorder(attemptId);
  const [state, setState] = useState<ScreeningState | null>(null);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);
  const submittingRef = useRef(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // The countdown re-anchors whenever the server answers, so drift
  // between the two can never accumulate.
  const [remaining, setRemaining] = useState<number | null>(null);
  const anchorRef = useRef<{ at: number; seconds: number } | null>(null);

  const apply = useCallback((next: ScreeningState) => {
    setState(next);
    if (next.seconds_remaining == null) {
      anchorRef.current = null;
      setRemaining(null);
      return;
    }
    anchorRef.current = { at: performance.now(), seconds: next.seconds_remaining };
    setRemaining(next.seconds_remaining);
  }, []);

  useEffect(() => {
    if (recorder.stream && videoRef.current) {
      videoRef.current.srcObject = recorder.stream;
    }
  }, [recorder.stream]);

  // Poll: the state AND the heartbeat. `last_seen_at` moving is what
  // lets the server work out how long a candidate was gone and give the
  // time back, so this is not a spare request.
  useEffect(() => {
    if (!attemptId) return undefined;
    let live = true;
    const read = async () => {
      try {
        const next = await fetchScreeningState(attemptId);
        if (live) apply(next);
      } catch {
        // Silent on purpose: losing the network mid-answer is the case
        // the whole round is designed around, and a toast per failed
        // poll would bury the candidate in noise while they speak.
      }
    };
    // ONE READ ALWAYS, so the page can tell whether the round is
    // finished, running or not yet begun. But the REPEAT only while
    // they are actually answering -- see below.
    void read();
    if (!checked) return () => {
      live = false;
    };

    // THIS POLL IS THE HEARTBEAT, so it must not beat while the
    // candidate cannot answer.
    //
    // `last_seen_at` moving is how the server decides they are present,
    // and it is what `absorb_absence` measures a pause against. Polling
    // on a page where the camera is OFF -- after a reload, before they
    // press Resume -- told the server they were answering while they
    // were locked out of doing so. Their allowance drained, the client
    // advanced when it hit zero, and a question they never got to speak
    // to was closed. On the last item that ended the sitting outright.
    const id = window.setInterval(read, POLL_MS);
    return () => {
      live = false;
      window.clearInterval(id);
    };
  }, [attemptId, apply, checked]);

  // Tick locally between polls -- and ONLY while answering, for the
  // same reason the poll does. A clock counting down on a screen whose
  // camera is off is telling the candidate they are losing time they
  // are not, in fact, losing.
  useEffect(() => {
    if (remaining == null || !checked) return undefined;
    const id = window.setInterval(() => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const gone = (performance.now() - anchor.at) / 1000;
      setRemaining(Math.max(0, Math.round(anchor.seconds - gone)));
    }, 500);
    return () => window.clearInterval(id);
  }, [remaining == null, checked]);

  const advance = useCallback(
    async (endedBy: "next" | "timer") => {
      if (busy || !state || state.finished) return;
      setBusy(true);
      try {
        apply(await advanceScreening(attemptId, recorder.elapsed, endedBy));
      } catch {
        toast({
          title: "Could not move to the next question",
          description: "Keep answering — this will retry on its own.",
          variant: "error",
        });
      } finally {
        setBusy(false);
      }
    },
    [attemptId, busy, state, recorder.elapsed, apply, toast],
  );

  // THE TIMER ADVANCES BY ITSELF. The candidate is mid-sentence when it
  // runs out and must not have to press anything; the server would close
  // the question anyway on the next request, so doing it here only makes
  // the screen agree with what has already happened.
  useEffect(() => {
    if (remaining !== 0 || !state || state.finished || busy) return;
    // NOT WHILE THE CAMERA IS OFF. Advancing CLOSES a question, and a
    // question closed while the candidate could not speak to it is an
    // answer they never got to give -- on the last item it ended the
    // whole sitting and submitted it.
    if (!checked || recorder.phase !== "recording") return;
    void advance("timer");
  }, [remaining, state, busy, advance, checked, recorder.phase]);

  const submit = useCallback(async () => {
    setBusy(true);
    try {
      await recorder.stop();
      await finaliseVideo(attemptId);
      recorder.release();
      toast({ title: "Screening submitted for assessor review" });
      navigate("/tests");
    } catch {
      // The automatic submit below gives up here and the button comes
      // back, because a candidate who cannot retry has lost a sitting
      // they get one of.
      submittingRef.current = false;
      setSubmitFailed(true);
      toast({
        title: "Could not submit",
        description:
          "Nothing you recorded has been lost. Try again in a moment.",
        variant: "error",
      });
      setBusy(false);
    }
  }, [attemptId, recorder, toast, navigate]);

  // SUBMITTING IS A CONSEQUENCE OF THE ROUND ENDING, NOT A SEPARATE ACT.
  //
  // There is no playback and no correction: once the last answer is
  // given there is nothing a candidate could still decide, so asking
  // them to press one more button only leaves the CAMERA RUNNING while
  // they work out what it is for. It fires on `finished` rather than in
  // the button handler so that the clock running out on the last
  // question ends the sitting exactly as pressing the button does.
  useEffect(() => {
    // `finished` stays true for a SUBMITTED attempt, so without the
    // status check a reload of a completed sitting would try to submit
    // it again -- stopping a recorder that was never started.
    if (!state?.finished || submittingRef.current) return;
    if (state.status !== "IN_PROGRESS") return;
    submittingRef.current = true;
    void submit();
  }, [state?.finished, state?.status, submit]);

  const beginRound = useCallback(async () => {
    setBusy(true);
    // TWO FAILURES WITH NOTHING IN COMMON, told apart.
    //
    // A bare `catch` around both of these reported "Could not start the
    // round" whether the camera had refused or the SERVER had -- and
    // when the server was refusing with a precise 409 (the attempt had
    // never been started), that message sent somebody looking at their
    // webcam. Same shape as `act()` swallowing a 422 on the reporting
    // screen; see CLAUDE.md, "catch {} cost the diagnosis".
    try {
      recorder.start();
    } catch {
      setBusy(false);
      toast({
        title: "Your camera or microphone could not be started",
        description:
          "Check that the browser is allowed to use them, then try again.",
        variant: "error",
      });
      return;
    }
    try {
      apply(await beginScreening(attemptId));
      setChecked(true);
      // FOLDED THE MOMENT THE RECORDING STARTS (asked for 2026-10-07): the
      // question is what to look at now, and the folded rail blinks red
      // so the camera is never forgotten. Unfolding stays one press away.
      setFolded(true);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      toast({
        title: detail ?? "Could not start the round",
        ...(detail
          ? {}
          : {
              description:
                "Nothing has been lost. Check your connection and try again.",
            }),
        variant: "error",
      });
    } finally {
      setBusy(false);
    }
  }, [attemptId, recorder, apply, toast]);

  const steps = useMemo(
    () => Array.from({ length: state?.total ?? 0 }, (_, i) => i),
    [state?.total],
  );

  const recording = recorder.phase === "recording";
  const question = state?.question ?? null;
  // A CASE ITEM. The candidate dictates a study rather than answering a
  // question, so the images need the room -- but the camera stays on
  // them, because the dictation is the part where knowing who is
  // speaking matters most.
  const caseId =
    (question as { assignment_case_id?: string } | null)?.assignment_case_id ??
    null;
  const progress = state && state.total > 0 ? (state.answered / state.total) * 100 : 0;
  // The reading room's dock while it is full screen. The camera card and
  // the question MOVE into it -- the recording is one continuous take with
  // no retake, so nothing here may remount.
  const [dock, setDock] = useState<HTMLElement | null>(null);
  // The camera column FOLDED to a slim panel, by the candidate's choice.
  // Kept across questions: somebody who wanted the room once wants it
  // for the next item too.
  const [folded, setFolded] = useState(false);
  const lastQuestion = !!state && state.index >= state.total - 1;
  const name = user?.full_name || user?.username || "Candidate";

  // WHETHER THE ROUND HAS STARTED IS THE SERVER'S ANSWER, NOT A LOCAL
  // FLAG. `checked` lives in component state and a RELOAD resets it, so
  // the page offered "Start the round" again on a sitting that was
  // already over -- and pressing it turned the camera back on and
  // recorded a candidate who had finished. The attempt's own status is
  // the only thing that survives a refresh.
  const closed =
    !!state && state.status !== "NOT_STARTED" && state.status !== "IN_PROGRESS";
  const begun = !!state && state.status !== "NOT_STARTED";
  const started = checked || begun;
  // FOLDING ONLY WHILE THE ROUND RUNS. Before it, the camera card holds
  // "Enable camera" and "Start the round", which must never be out of
  // sight. And not while docked in the reading room's full screen, where
  // the dock already is the side panel.
  const running = checked && begun && !state?.finished;
  const foldedHere = folded && running && !(caseId && dock);

  if (closed) {
    // NOTHING TO OFFER AND NOTHING TO RECORD. One attempt, no retake:
    // there is no control on this screen a finished candidate should be
    // able to press, so none are drawn rather than drawn and disabled.
    return (
      <div className="sk-vr" data-cy="video-screening">
        <div className="sk-vr-page">
          <div className="sk-vr-card p-6">
            <h1 className="sk-vr-h1">This screening round is finished</h1>
            <p className="mt-2 text-[13.5px] text-[#5C6B65]" data-cy="vr-closed">
              {state?.status === "SUBMITTED"
                ? "Your answers have been submitted for assessment. There is nothing further to do."
                : "This sitting is closed and cannot be reopened."}
            </p>
            <button
              type="button"
              data-cy="btn-vr-back"
              className="sk-vr-btn mt-5"
              onClick={() => navigate("/tests")}
            >
              Back to my assessments
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`sk-vr${recording ? " sk-vr-recording" : ""}`} data-cy="video-screening">
      <div className="sk-vr-page">
        {/* ---- header ---- */}
        <div className="mb-[22px] flex flex-wrap items-start gap-6">
          <div className="min-w-0">
            <h1 className="sk-vr-h1">Candidate screening · radiology IMG</h1>
            <p className="mt-1.5 text-[13.5px] text-[#5C6B65]">
              Answer each question to camera. You cannot return to a question
              once you move on.
            </p>
          </div>
          <div className="ml-auto flex items-center gap-3.5">
            <div className="text-right">
              <div className="sk-vr-eyebrow">Candidate</div>
              <div className="sk-vr-name" data-cy="vr-candidate">
                {name}
              </div>
            </div>
            <div className="sk-vr-initials">{initialsOf(name)}</div>
            {/* ONLY WHEN THE AUTOMATIC SUBMIT FAILED. In the ordinary
                run the round submits itself the moment the last answer
                ends, so a button sitting here through the whole sitting
                counting "3/6" is an instruction to do something that is
                never the candidate's to do. It comes back if the submit
                could not be completed, because one attempt and no
                retake means they must have a way to try again. */}
            {submitFailed && (
              <button
                type="button"
                data-cy="btn-vr-submit"
                className="sk-vr-btn"
                disabled={busy}
                onClick={() => void submit()}
              >
                Submit screening
              </button>
            )}
          </div>
        </div>

        {/* ---- the stepper: state, not a control ---- */}
        <div
          className="mb-[18px] flex flex-wrap gap-[9px]"
          data-cy="vr-stepper"
          aria-label="Progress through the questions"
        >
          {steps.map((i) => {
            const done = !!state && i < state.answered;
            const current = !!state && i === state.index && !state.finished;
            return (
              <span
                key={i}
                aria-current={current ? "step" : undefined}
                className={[
                  "sk-vr-step",
                  current ? "sk-vr-step-on" : "",
                  done ? "sk-vr-step-done" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {done ? "✓ " : ""}
                Question {i + 1}
              </span>
            );
          })}
        </div>

        {/* ---- progress bar ---- */}
        <div className="sk-vr-card sk-vr-bar mb-3">
          <div className="flex items-center gap-[9px]">
            <span
              className={[
                "sk-vr-dot",
                recording ? "sk-vr-dot-rec" : recorder.stream ? "sk-vr-dot-on" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            />
            <span className="text-[13.5px] text-[#5C6B65]">
              {recording
                ? "Recording in progress"
                : recorder.stream
                  ? "Camera and microphone ready"
                  : "Camera not connected"}
            </span>
          </div>
          <div className="h-5 w-px bg-[#E4E9E7]" />
          <div className="text-[13.5px] text-[#5C6B65]" data-cy="vr-progress">
            {state?.answered ?? 0} of {state?.total ?? 0} answered
          </div>
          <div className="sk-vr-track">
            <span style={{ width: `${progress}%` }} />
          </div>
          {!!state && state.disconnections > 0 && (
            <div className="text-[13.5px] text-[#8A6410]" data-cy="vr-drops">
              {state.disconnections} of {state.max_disconnections} connection
              drops used
            </div>
          )}
          <div className="ml-auto flex items-center gap-2.5">
            <span className="sk-vr-eyebrow">This question</span>
            <span className="sk-vr-clock" data-cy="vr-clock">
              {remaining == null ? "--:--" : mmss(remaining)}
            </span>
          </div>
        </div>

        {/* ---- the two panes ---- */}
        {/* THE CAMERA IS ALWAYS ON THE LEFT, AND THE CONTROLS NEVER
            MOVE. It used to be the second child of a flex row on a case
            item -- study left, self-view pinned right -- so the card
            crossed the screen between items and took "Next question"
            with it, under the cursor of somebody about to press it.
            First in the flow now, in both layouts, so its left edge and
            the left-aligned controls inside it stay where they were.

            What DOES change on a case is its width: the study is the
            thing being read, so the camera narrows to 420px and gives
            the rest away. A self-view only has to show a face.

            AND IT IS NOT STICKY ON A CASE. The question rows sit in a
            full-width row underneath, and a sticky card in the row
            above is a card they scroll UNDERNEATH -- the list
            disappearing behind the video was exactly that. */}
        {/* ONE LAYOUT FOR EVERY ITEM (2026-10-07, asked for): the camera
            in a 420px column on the left, the study and the question to
            its right -- on a plain question exactly as on a case. The
            plain question used to give the camera half the screen, so the
            self-view changed size between items for no reason a candidate
            could see. The column folds to a slim panel and back. */}
        <div className="sk-vr-layout">
          <div className={`sk-vr-side${foldedHere ? " sk-vr-side-folded" : ""}`}>
          {foldedHere && (
            <div
              className={`sk-vr-rail${recording ? " sk-vr-rail-rec" : ""}`}
              data-cy="vr-camera-folded"
              data-recording={recording ? "true" : "false"}
            >
              <button
                type="button"
                data-cy="btn-vr-unfold"
                className="sk-vr-railbtn"
                onClick={() => setFolded(false)}
                title="Show the camera"
                aria-label="Show the camera"
              >
                ›
              </button>
              <span
                className={`sk-vr-dot${recording ? " sk-vr-raildot-rec" : recorder.stream ? " sk-vr-dot-on" : ""}`}
                aria-hidden
              />
              <span className="sk-vr-railtext">{recording ? "Recording" : "Standby"}</span>
              <span className="sk-vr-railclock">{mmss(recorder.elapsed)}</span>
            </div>
          )}
          {/* camera -- moved into the reading room's dock in full screen.
              HIDDEN, never unmounted, when folded: the preview keeps its
              stream and the recording never notices. */}
          <Movable to={caseId ? dock : null}>
          <div className={["sk-vr-card overflow-hidden", foldedHere ? "hidden" : ""].join(" ")}>
            <div className="sk-vr-videohead">
              <span className={`sk-vr-recbadge${recording ? " sk-vr-recbadge-on" : ""}`}>
                {recording ? "Recording" : "Standby"}
              </span>
              <span className="sk-vr-clock ml-auto text-[rgba(255,255,255,0.75)]">
                {mmss(recorder.elapsed)}
              </span>
              {running && !(caseId && dock) && (
                <button
                  type="button"
                  data-cy="btn-vr-fold"
                  className="sk-vr-foldbtn"
                  onClick={() => setFolded(true)}
                  title="Fold the camera to a side panel"
                  aria-label="Fold the camera to a side panel"
                >
                  ‹
                </button>
              )}
            </div>
            <div className="sk-vr-videobody">
              <div className="sk-vr-stage">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  data-cy="vr-preview"
                  className="block h-full w-full scale-x-[-1] object-cover"
                />
                {!recorder.stream && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3.5 bg-[#0E1613] p-6 text-center">
                    <div className="grid h-[62px] w-[62px] place-items-center rounded-full border-2 border-[rgba(255,255,255,0.22)]" />
                    <div>
                      <div className="text-[14.5px] font-semibold text-white">
                        {recorder.error ? "Camera blocked" : "Camera is off"}
                      </div>
                      <div className="mx-auto mt-1 max-w-[280px] text-[12.5px] leading-[1.5] text-[rgba(255,255,255,0.5)]">
                        {recorder.error ??
                          "We need your camera and microphone to record answers."}
                      </div>
                    </div>
                    <button
                      type="button"
                      data-cy="btn-vr-enable"
                      className="sk-vr-btn"
                      onClick={() => void recorder.enable()}
                    >
                      Enable camera
                    </button>
                  </div>
                )}
                {recording && (
                  <div className="pointer-events-none absolute inset-0 rounded-lg border-[3px] border-[#D64545]" />
                )}
                {!!question && recorder.stream && (
                  <div className="sk-vr-caption">
                    <div className="sk-vr-kicker">
                      Question {(state?.index ?? 0) + 1} of {state?.total ?? 0}
                    </div>
                    <div className="sk-vr-prompt" data-cy="vr-prompt">
                      {question.prompt}
                    </div>
                  </div>
                )}
              </div>

              {/* level meter, from the stream the recorder muxes */}
              <div className="mt-3.5 flex items-center gap-3">
                <span className="sk-vr-eyebrow">{recording ? "Live" : "Ready"}</span>
                <div className="sk-vr-meter" data-cy="vr-meter" aria-hidden>
                  {Array.from({ length: 44 }, (_, i) => {
                    const env = Math.sin((i / 44) * Math.PI);
                    const h = Math.max(3, recorder.level * 26 * (0.45 + env * 0.85));
                    return <span key={i} style={{ height: `${h}px` }} />;
                  })}
                </div>
              </div>

              {/* Not rendered while folded: the question's own header carries
                  "Next question" then, and two of it would be one too many. */}
              {!foldedHere && (
              <div className="mt-4 flex flex-wrap items-center gap-2.5">
                {!started ? (
                  <button
                    type="button"
                    data-cy="btn-vr-begin"
                    className="sk-vr-btn"
                    disabled={!recorder.stream || busy}
                    onClick={() => void beginRound()}
                  >
                    Start the round
                  </button>
                ) : begun && !checked ? (
                  // RESUMED, NOT STARTED. The round is already running
                  // on the server -- a reload, a flat battery, a closed
                  // lid -- so the camera has to come back before they
                  // can answer, and calling it "Start" would suggest
                  // the clock had not been going.
                  <button
                    type="button"
                    data-cy="btn-vr-resume"
                    className="sk-vr-btn"
                    disabled={!recorder.stream || busy}
                    onClick={() => void beginRound()}
                  >
                    Resume the round
                  </button>
                ) : (
                  <button
                    type="button"
                    data-cy="btn-vr-next"
                    className="sk-vr-btn"
                    disabled={busy || !state || state.finished}
                    onClick={() => void advance("next")}
                  >
                    {lastQuestion ? "Finish and submit" : "Next question"}
                  </button>
                )}
                <div className="ml-auto text-right">
                  <div className="sk-vr-eyebrow">Allotted</div>
                  <div className="sk-vr-clock" data-cy="vr-allotted">
                    {mmss(
                      Number(
                        (question?.config as { allotted_seconds?: number } | null)
                          ?.allotted_seconds ?? 0,
                      ),
                    )}
                  </div>
                </div>
              </div>
              )}

              <div className="sk-vr-note">
                <div className="sk-vr-note-h">Before you record</div>
                <div className="sk-vr-note-b">
                  Face a light source, keep your whole head in frame, and speak at
                  a normal pace. Each question is recorded once — there is no
                  retake, and you cannot return to an earlier question.
                </div>
              </div>
            </div>
          </div>
          </Movable>
          </div>

          <div className="sk-vr-main">
          {/* THE STUDY. It takes whatever the camera does not, which is
              the point of narrowing the camera: a stack read at
              postage-stamp size is not a reading.

              The explicit height is not decoration -- the viewer sizes
              itself to its parent, and in a row whose other child is a
              self-view the card had no height of its own to give it. */}
          {caseId && (
            <div
              className="sk-vr-card h-[68vh] min-h-[460px] min-w-0 overflow-hidden p-3"
              data-cy="vr-case-viewer"
            >
              {/* THE READING ROOM, embedded (2026-10-07; scope section
                  11). The round is not yet client-approved, and this is
                  part of what the approval will now be given for. Full
                  screen docks the camera and the question on the left. */}
              <RoomViewer
                source={{
                  kind: "attempt",
                  attemptId,
                  assignmentCaseId: caseId,
                }}
                testCase={{
                  title: question?.section_title || "Case",
                  modality: null,
                  clinical_history: null,
                }}
                dockSide="left"
                onDock={setDock}
                dockLabel="Camera & question"
                dockStrip={
                  <>
                    <span
                      className={`rr-strip-dot${recording ? " rr-strip-dot--rec" : ""}`}
                      data-cy="dock-strip-rec"
                      data-recording={recording ? "true" : "false"}
                      title={recording ? "Recording" : "Standby"}
                    />
                    <span className="rr-strip-clock">
                      {remaining == null ? "--:--" : mmss(remaining)}
                    </span>
                  </>
                }
                // THE CAMERA COLUMN FOLDED IN FULL SCREEN: the camera stays
                // live in it, hidden; what a candidate must still see and
                // press -- that it is recording, the question and its time,
                // and the way on -- goes in the bar across the top.
                dockBar={
                  <div className="rr-vrbar" data-cy="vr-dockbar">
                    <span
                      className={`rr-vrbar-rec${recording ? " rr-vrbar-rec--on" : ""}`}
                    >
                      <span className={`rr-strip-dot${recording ? " rr-strip-dot--rec" : ""}`} />
                      {recording ? "Recording" : "Standby"}
                      <span className="rr-vrbar-time">{mmss(recorder.elapsed)}</span>
                    </span>
                    <span className="rr-vrbar-q">
                      {state?.finished
                        ? "All questions answered"
                        : `Question ${(state?.index ?? 0) + 1} of ${state?.total ?? 0}`}
                    </span>
                    <span className="rr-vrbar-prompt" data-cy="vr-dockbar-question" title={question?.prompt}>
                      {question?.prompt}
                    </span>
                    <span className={`rr-vrbar-time${recording ? " rr-vrbar-time--rec" : ""}`}>
                      {remaining == null ? "--:--" : mmss(remaining)}
                    </span>
                    <button
                      type="button"
                      data-cy="btn-vr-next-bar"
                      className="sk-vr-btn sk-vr-btn-onbar"
                      disabled={busy || !state || state.finished}
                      onClick={() => void advance("next")}
                    >
                      {lastQuestion ? "Finish and submit" : "Next question"}
                    </button>
                  </div>
                }
              />
            </div>
          )}

          {/* the question -- docked under the camera in full screen */}
          <Movable to={caseId ? dock : null}>
          <div className="flex min-w-0 flex-col gap-2.5">
            <div className="flex items-center gap-3 rounded-[10px] bg-[#1F6B4A] px-5 py-3">
              <div className="text-[13px] font-semibold text-white">
                {state?.finished
                  ? "All questions answered"
                  : `Question ${(state?.index ?? 0) + 1} of ${state?.total ?? 0}`}
              </div>
              {/* WITH THE CAMERA FOLDED, "Next question" lives here -- the
                  card it normally sits in is out of sight, and a candidate
                  must always be able to move on. */}
              {foldedHere && (
                <button
                  type="button"
                  data-cy="btn-vr-next"
                  className="sk-vr-btn sk-vr-btn-onbar ml-auto"
                  disabled={busy || !state || state.finished}
                  onClick={() => void advance("next")}
                >
                  {lastQuestion ? "Finish and submit" : "Next question"}
                </button>
              )}
            </div>

            {!!question && (
              <div
                data-cy="vr-question-row"
                className={[
                  "sk-vr-row",
                  recording ? "sk-vr-row-rec" : "sk-vr-row-on",
                ].join(" ")}
              >
                <div className="flex items-start gap-[13px]">
                  <div className="sk-vr-num sk-vr-num-on">
                    {String((state?.index ?? 0) + 1).padStart(2, "0")}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-[9px]">
                      <span className="sk-vr-rowkicker sk-vr-rowkicker-on">
                        {question.section_title || "Question"}
                      </span>
                      <span
                        className={`sk-vr-badge ${recording ? "sk-vr-badge-rec" : "sk-vr-badge-on"}`}
                      >
                        {recording ? "Recording" : "Up next"}
                      </span>
                    </div>
                    <div className="sk-vr-rowtext sk-vr-rowtext-on" data-cy="vr-question-text">
                      {question.prompt}
                    </div>
                    {!!question.help_text && (
                      <div className="mt-1.5 text-[12.5px] leading-[1.6] text-[#5C6B65]">
                        {question.help_text}
                      </div>
                    )}
                  </div>
                  <div className="flex-none pl-2 text-right">
                    <div className="sk-vr-eyebrow">Remaining</div>
                    <div
                      className={`sk-vr-time${recording ? " sk-vr-time-rec" : ""}`}
                    >
                      {remaining == null ? "--:--" : mmss(remaining)}
                    </div>
                  </div>
                </div>
                <div className={`sk-vr-wave${recording ? " sk-vr-wave-on" : ""}`}>
                  <div aria-hidden>
                    {Array.from({ length: 62 }, (_, i) => (
                      <span
                        key={i}
                        style={{
                          height: `${recording ? Math.max(3, recorder.level * 34 * (0.4 + Math.sin((i / 62) * Math.PI))) : 3}px`,
                          background: recording ? "#D64545" : "#E4E9E7",
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Answered questions, shown but not re-enterable. FUTURE
                questions are deliberately absent: revealing them early
                would let a candidate prepare an answer the round exists
                to hear unrehearsed. */}
            {!!state &&
              Array.from({ length: state.answered }, (_, i) => (
                <div key={i} className="sk-vr-row opacity-70" data-cy="vr-answered-row">
                  <div className="flex items-center gap-[13px]">
                    <div className="sk-vr-num sk-vr-num-done">✓</div>
                    <div className="min-w-0 flex-1">
                      <span className="sk-vr-rowkicker">Question {i + 1}</span>
                      <div className="sk-vr-rowtext">Answered</div>
                    </div>
                    <span className="sk-vr-badge sk-vr-badge-done">Answered</span>
                  </div>
                </div>
              ))}

            <div className="sk-vr-card sk-vr-bar">
              <span className="text-[13px] text-[#5C6B65]">
                {state?.finished
                  ? "Every question is answered. Submit to send your screening."
                  : "Your recording is uploading as you go."}
              </span>
              <span className="ml-auto text-[12px] text-[#8A9891]" data-cy="vr-upload">
                {recorder.pending > 0
                  ? `${recorder.pending} waiting to upload`
                  : `${recorder.uploaded} uploaded`}
              </span>
            </div>

            <button
              type="button"
              data-cy="btn-vr-leave"
              className="self-start text-[13px] text-[#5C6B65] underline"
              onClick={() => setConfirmLeave(true)}
            >
              Leave without submitting
            </button>
          </div>
          </Movable>
          </div>
        </div>
      </div>

      <AppDialog
        open={confirmLeave}
        cy="dialog-vr-leave"
        tone="danger"
        title="Leave without submitting?"
        body="You get one attempt at this round. Everything recorded so far is kept and will be assessed, but you cannot come back and continue."
        onDismiss={() => setConfirmLeave(false)}
        actions={[
          {
            label: "Leave",
            cy: "btn-vr-confirm-leave",
            variant: "danger",
            onClick: () => {
              recorder.release();
              navigate("/tests");
            },
          },
          {
            label: "Keep answering",
            cy: "btn-vr-cancel-leave",
            variant: "ghost",
            // FOCUS LANDS HERE, not on Leave. The destructive action is
            // written first because it is what the question is about,
            // and focusing it would make Enter end a sitting somebody
            // gets one of.
            autoFocus: true,
            onClick: () => setConfirmLeave(false),
          },
        ]}
      />
    </div>
  );
}
